import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import * as React from "react";

import { cn } from "../../utils/cn";

import { textVariants } from "./text";

function Popover({
    ...props
}: PopoverPrimitive.Root.Props): React.ReactElement {
    return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({
    ...props
}: PopoverPrimitive.Trigger.Props): React.ReactElement {
    return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverClose({
    ...props
}: PopoverPrimitive.Close.Props): React.ReactElement {
    return <PopoverPrimitive.Close data-slot="popover-close" {...props} />;
}

function PopoverContent({
    className,
    align = "center",
    alignOffset = 0,
    side = "bottom",
    sideOffset = 6,
    anchor,
    ...props
}: PopoverPrimitive.Popup.Props &
    Pick<
        PopoverPrimitive.Positioner.Props,
        "align" | "alignOffset" | "side" | "sideOffset" | "anchor"
    >): React.ReactElement {
    return (
        <PopoverPrimitive.Portal>
            <PopoverPrimitive.Positioner
                align={align}
                alignOffset={alignOffset}
                side={side}
                sideOffset={sideOffset}
                anchor={anchor}
                className="isolate z-50"
            >
                <PopoverPrimitive.Popup
                    data-slot="popover-content"
                    className={cn(
                        textVariants({ variant: "body" }),
                        "z-50 flex w-72 min-w-[8rem] origin-(--transform-origin) flex-col gap-2.5 rounded-none border border-line-base bg-paper-200 p-2.5 text-foreground-base outline-hidden duration-100",
                        "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
                        "data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2",
                        "data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2",
                        "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95",
                        "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
                        className
                    )}
                    {...props}
                />
            </PopoverPrimitive.Positioner>
        </PopoverPrimitive.Portal>
    );
}

function PopoverHeader({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
    return (
        <div
            data-slot="popover-header"
            className={cn(
                textVariants({ variant: "body" }),
                "flex flex-col gap-0.5",
                className
            )}
            {...props}
        />
    );
}

function PopoverTitle({
    className,
    ...props
}: PopoverPrimitive.Title.Props): React.ReactElement {
    return (
        <PopoverPrimitive.Title
            data-slot="popover-title"
            className={cn(textVariants({ variant: "body" }), className)}
            {...props}
        />
    );
}

function PopoverDescription({
    className,
    ...props
}: PopoverPrimitive.Description.Props): React.ReactElement {
    return (
        <PopoverPrimitive.Description
            data-slot="popover-description"
            className={cn(
                textVariants({ variant: "body" }),
                "text-foreground-neutral",
                className
            )}
            {...props}
        />
    );
}

export {
    Popover,
    PopoverTrigger,
    PopoverClose,
    PopoverContent,
    PopoverHeader,
    PopoverTitle,
    PopoverDescription
};
